import * as React from 'react';
import styled from 'styled-components';
import Link from 'next/link';

import { getPublicRuntimeConfig } from '@helpers/getPublicRuntimeConfig';
import { isValidEmail, REGISTERED_SHA256 } from '@packages/common';
import { useTranslations } from '@packages/i18n';
import { NewsletterFooterSubmitMessage, SideEffectsContext, useNewsletterCallbacks } from '@packages/shared';
import { enrollForNewsletter } from '@packages/services';
import { Anchor, Button, InputAnimated, InputPopover, media, PopoverPosition, Text, themeGet } from '@packages/ui';

const SubmitButton = styled(Button)`
  font-weight: bold;
`;

const ShowOnLargeDisplay = styled.div`
  ${media.to.md`
    display: none;
  `}
`;

const ShowOnSmallDisplay = styled.div`
  ${media.from.md`
    display: none;
  `}
`;

const EMailInput = styled(InputAnimated)`
  display: inline-block;
  width: 288px;
`;

const RowContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Container = styled.form`
  > div {
    margin-top: ${themeGet.space(2)};
  }
`;

const InputHolder = styled.div`
  position: relative;
  ${media.from.md`
      padding-right: ${themeGet.space(3)};
    `}
`;

export interface SimpleNewsletterProps {
  tag?: string;
}

const APP_TLD = getPublicRuntimeConfig('APP_TLD');
const ROUTES = getPublicRuntimeConfig('ROUTES');
const BRIDGE_URL = getPublicRuntimeConfig('BRIDGE_URL');

export const SimpleNewsletter: React.FC<SimpleNewsletterProps> = ({ tag }) => {
  const [
    emailPlaceholder,
    subscribeCaption,
    dataProtectionNote,
    dataProtectionLinkLabel,
    invalidEmailErrorMessage,
    generalErrorMessage,
    emailAlreadyRegisteredMessage,
    successText,
    successText2,
  ] = useTranslations([
    'newsletterFooterSignUpButtonPlaceholder',
    'newsletterFooterSignUpButtonLabel',
    'newsletterFooterDataProtectionNote',
    'newsletterFooterDataProtectionLinkLabel',
    'newsletterFooterInvalidEmailErrorMessage',
    'newsletterFooterGeneralErrorMessage',
    'newsletterFooterEmailAlreadyRegisteredMessage',
    'newsletterFooterSubmitSuccessMessage',
    'newsletterFooterSubmitSuccessMessage2',
  ]);
  const { newsletterSubmit$ } = useNewsletterCallbacks();
  const { newsletterRegistered } = React.useContext(SideEffectsContext);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [disableSubmitButton, setDisableSubmitButton] = React.useState(false);
  const handleChange = React.useCallback((_: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMessage(null);
  }, []);

  const [submitMessage, setSubmitMessage] = React.useState(successText);

  const handleSubmit = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.stopPropagation();
      e.preventDefault();
      const email = (e.currentTarget[0] as HTMLInputElement).value;
      if (!isValidEmail(email)) {
        setErrorMessage(invalidEmailErrorMessage);
        return;
      }

      setDisableSubmitButton(true);
      enrollForNewsletter(BRIDGE_URL, { email, tld: APP_TLD, tag })
        .then(({ email_sha256: sha256 }) => {
          setIsSubmitted(true);
          setErrorMessage(null);
          if (sha256 === REGISTERED_SHA256) {
            setSubmitMessage(successText2);
          } else {
            newsletterSubmit$.next(sha256);
            newsletterRegistered();
            setSubmitMessage(successText);
          }
        })
        .catch((err: any) => {
          setIsSubmitted(false);
          if (err) {
            setErrorMessage(
              ({ contact_key: invalidEmailErrorMessage, id: emailAlreadyRegisteredMessage } as any)[err.error_key] ||
                generalErrorMessage,
            );
          }
        })
        .finally(() => {
          setDisableSubmitButton(false);
        });
    },
    [
      emailAlreadyRegisteredMessage,
      generalErrorMessage,
      invalidEmailErrorMessage,
      newsletterRegistered,
      newsletterSubmit$,
      successText,
      successText2,
      tag,
    ],
  );

  const dataProctectionUrl = (ROUTES.dataProtection as string) || '#';
  return isSubmitted ? (
    <Container>
      <NewsletterFooterSubmitMessage submitMessage={submitMessage} />
    </Container>
  ) : (
    <Container onSubmit={handleSubmit}>
      <RowContainer>
        <InputHolder>
          <EMailInput hasError={!!errorMessage} onChange={handleChange} placeholder={emailPlaceholder}>
            {errorMessage && <InputPopover position={PopoverPosition.Top}>{errorMessage}</InputPopover>}
          </EMailInput>
        </InputHolder>
        <ShowOnLargeDisplay>
          <SubmitButton type="submit" disabled={disableSubmitButton}>
            {subscribeCaption}
          </SubmitButton>
        </ShowOnLargeDisplay>
      </RowContainer>
      <RowContainer>
        {!isSubmitted && (
          <Text tag="span" fontSize={4}>
            {dataProtectionNote}{' '}
            <Link href={dataProctectionUrl}>
              <Anchor href={dataProctectionUrl} prominentStyle={true} target="_blank">
                {dataProtectionLinkLabel}
              </Anchor>
            </Link>
          </Text>
        )}
      </RowContainer>
      <RowContainer>
        <ShowOnSmallDisplay>
          <SubmitButton type="submit" disabled={disableSubmitButton}>
            {subscribeCaption}
          </SubmitButton>
        </ShowOnSmallDisplay>
      </RowContainer>
    </Container>
  );
};
